import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Mail, CheckCircle, Send } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ContactPage() {
  const [form, setForm] = useState({
    name: '',
    email: '',
    company: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.post(`${API}/api/contact`, form);
      setSuccess(true);
      setForm({ name: '', email: '', company: '', subject: '', message: '' });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const offices = [
    {
      city: 'London',
      type: 'Headquarters',
      address: '45 Albemarle Street\nLondon W1S 4JL\nUnited Kingdom'
    },
    {
      city: 'Amsterdam',
      type: 'Office',
      address: 'Keizersgracht 452\n1016 GD Amsterdam\nThe Netherlands'
    },
    {
      city: 'Milan',
      type: 'Office',
      address: 'Via Monte Napoleone 8\n20121 Milano\nItaly'
    },
    {
      city: 'Dubai',
      type: 'Office',
      address: 'DIFC Gate Village\nDubai\nUnited Arab Emirates'
    },
    {
      city: 'Paris',
      type: 'Office',
      address: '8 Avenue Montaigne\n75008 Paris\nFrance'
    }
  ];

  return (
    <div className="bg-white pt-32 pb-24">
      {/* Header */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 mb-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-3xl"
        >
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-4">Contact</p>
          <h1 className="font-serif text-4xl lg:text-5xl text-[#0A192F] tracking-tight mb-6">
            Get in Touch
          </h1>
          <p className="text-lg text-slate-600 leading-relaxed">
            We welcome inquiries from potential partners, investors, and management teams 
            of companies that share our vision of long-term value creation.
          </p>
        </motion.div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            {success ? (
              <div className="bg-emerald-50 border border-emerald-200 p-8 text-center">
                <CheckCircle className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
                <h3 className="font-serif text-2xl text-[#0A192F] mb-2">Thank You</h3>
                <p className="text-slate-600">
                  Your message has been received. We will respond within 2 business days.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-xs uppercase tracking-wider text-slate-500 mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      required
                      data-testid="contact-name-input"
                      className="w-full px-4 py-3 border border-slate-200 focus:border-[#0A192F] focus:ring-0 outline-none transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-wider text-slate-500 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      required
                      data-testid="contact-email-input"
                      className="w-full px-4 py-3 border border-slate-200 focus:border-[#0A192F] focus:ring-0 outline-none transition-colors"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-xs uppercase tracking-wider text-slate-500 mb-2">
                      Company
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={form.company}
                      onChange={handleChange}
                      data-testid="contact-company-input"
                      className="w-full px-4 py-3 border border-slate-200 focus:border-[#0A192F] focus:ring-0 outline-none transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-wider text-slate-500 mb-2">
                      Subject *
                    </label>
                    <select
                      name="subject"
                      value={form.subject}
                      onChange={handleChange}
                      required
                      data-testid="contact-subject-select"
                      className="w-full px-4 py-3 border border-slate-200 focus:border-[#0A192F] focus:ring-0 outline-none transition-colors bg-white"
                    >
                      <option value="">Select a subject</option>
                      <option value="Investment Opportunity">Investment Opportunity</option>
                      <option value="Partnership Inquiry">Partnership Inquiry</option>
                      <option value="Investor Relations">Investor Relations</option>
                      <option value="Media Inquiry">Media Inquiry</option>
                      <option value="General Inquiry">General Inquiry</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs uppercase tracking-wider text-slate-500 mb-2">
                    Message *
                  </label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    data-testid="contact-message-input"
                    className="w-full px-4 py-3 border border-slate-200 focus:border-[#0A192F] focus:ring-0 outline-none transition-colors resize-none"
                  />
                </div>

                {error && (
                  <p className="text-red-600 text-sm" data-testid="contact-error">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit-btn"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-[#0A192F] text-white text-sm uppercase tracking-wider hover:bg-[#112240] transition-colors disabled:opacity-50"
                >
                  {loading ? 'Sending...' : 'Send Message'}
                  <Send className="w-4 h-4" />
                </button>
              </form>
            )}
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div className="mb-12">
              <h2 className="font-serif text-2xl text-[#0A192F] mb-6">Our Offices</h2>
              <div className="grid gap-6">
                {offices.map((office, index) => (
                  <div key={index} className="flex gap-4">
                    <MapPin className="w-5 h-5 text-slate-400 flex-shrink-0 mt-1" />
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-[#0A192F]">{office.city}</span>
                        {office.type === 'Headquarters' && (
                          <span className="px-2 py-0.5 bg-[#0A192F] text-white text-[10px] uppercase tracking-wider">
                            HQ
                          </span>
                        )}
                      </div>
                      <address className="not-italic text-sm text-slate-600 whitespace-pre-line">
                        {office.address}
                      </address>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-12">
              <h2 className="font-serif text-2xl text-[#0A192F] mb-6">Contact Departments</h2>
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <Mail className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">General Inquiries</p>
                    <a href="mailto:info@bndvcapital.com" className="text-[#0A192F] hover:underline">
                      info@bndvcapital.com
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <Mail className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">Investor Relations</p>
                    <a href="mailto:ir@bndvcapital.com" className="text-[#0A192F] hover:underline">
                      ir@bndvcapital.com
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <Mail className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">Media & Press</p>
                    <a href="mailto:press@bndvcapital.com" className="text-[#0A192F] hover:underline">
                      press@bndvcapital.com
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-8 bg-[#F8F9FA]">
              <h3 className="font-medium text-[#0A192F] mb-4">Investment Opportunities</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                If you are a management team or advisor representing a company that could be 
                a fit for Dolchester Holding's portfolio, we would be pleased to hear from you. 
                Please include a brief description of the opportunity in your message.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
